import React, { useState, useEffect, useMemo } from 'react'
import { 
  ArrowLeft,
  Save,
  RotateCcw,
  Plus,
  Trash2,
  FileSignature
} from 'lucide-react'
import Breadcrumb from '../../components/Common/Breadcrumb'
import { useNavigate, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import signedContractService from '../../services/signedContractService'
import draftContractService from '../../services/draftContractService'
import customerMasterService from '../../services/customerMasterService'
import projectService from '../../services/projectService'
import { useContractTypeOptions } from './useContractTypeOptions'

const COMPLIANCE_STATUS_OPTIONS = [
  { value: 'Compliant', label: 'Compliant' },
  { value: 'Non-Compliant', label: 'Non-Compliant' },
  { value: 'Under Review', label: 'Under Review' },
  { value: 'Not Applicable', label: 'N/A' }
]

const toDateInput = (value) => (value ? new Date(value).toISOString().split('T')[0] : '')

const SignedContractForm = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const isEditMode = location.pathname.includes('/edit')
  const isViewMode = location.pathname.includes('/view')
  // Report drill-downs link with ?id=, so fall back to it when no router state is present
  const queryContractId = new URLSearchParams(location.search).get('id')
  const stateContractData = location.state?.contractData
  const contractData = useMemo(
    () => stateContractData || (queryContractId ? { _id: queryContractId } : undefined),
    [stateContractData, queryContractId]
  )
  const draftContractId = location.state?.draftContractId

  const [formData, setFormData] = useState({
    contractNumber: '',
    draftContract: '',
    contractTitle: '',
    contractDescription: '',
    contractType: 'Service Agreement',
    partyType: 'Customer',
    party: '',
    partyName: '',
    partyCode: '',
    partyContactPerson: '',
    partyEmail: '',
    partyAddress: {
      street: '',
      city: '',
      state: '',
      country: '',
      zipCode: ''
    },
    contractStartDate: '',
    contractEndDate: '',
    contractDuration: 0,
    signedDate: new Date().toISOString().split('T')[0],
    signedByName: '',
    partySignedDate: '',
    partySignedBy: '',
    partySignedByName: '',
    contractValue: 0,
    currency: 'INR',
    paymentTerms: '',
    billingFrequency: 'Monthly',
    project: '',
    projectCode: '',
    projectName: '',
    termsAndConditions: '',
    keyTerms: [],
    deliverables: [],
    milestones: [],
    complianceRequirements: [],
    complianceStatus: 'Compliant',
    lastComplianceReview: '',
    nextComplianceReview: '',
    isRenewable: false,
    renewalDate: '',
    renewalNoticePeriod: 30,
    autoRenewal: false,
    notes: '',
    internalNotes: '',
    status: 'Active'
  })

  const [customers, setCustomers] = useState([])
  const [projects, setProjects] = useState([])
  const [draftContracts, setDraftContracts] = useState([])
  const [isLoadingContractNumber, setIsLoadingContractNumber] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [newKeyTerm, setNewKeyTerm] = useState({ term: '', description: '' })
  const [newDeliverable, setNewDeliverable] = useState('')
  const [newMilestone, setNewMilestone] = useState({ milestoneName: '', milestoneDate: '', milestoneAmount: 0, description: '', status: 'Pending' })
  const [newCompliance, setNewCompliance] = useState('')
  const { options: contractTypeOptions } = useContractTypeOptions(formData.contractType)

  // Fetch next contract number (only for new contracts)
  useEffect(() => {
    if (!isEditMode && !isViewMode) {
      const fetchContractNumber = async () => {
        try {
          setIsLoadingContractNumber(true)
          const response = await signedContractService.getNextContractNumber()
          if (response.success && response.data) {
            setFormData(prev => ({
              ...prev,
              contractNumber: response.data.contractNumber || response.data.formattedValue || 'SC0001'
            }))
          }
        } catch (error) {
          console.error('Error fetching contract number:', error)
          setFormData(prev => ({ ...prev, contractNumber: 'SC0001' }))
        } finally {
          setIsLoadingContractNumber(false)
        }
      }
      fetchContractNumber()
    }
  }, [isEditMode, isViewMode])

  // Load customers, projects, and draft contracts
  useEffect(() => {
    loadCustomers()
    loadProjects()
    loadDraftContracts()
  }, [])

  // Load contract data if editing or viewing
  useEffect(() => {
    if ((isEditMode || isViewMode) && contractData?._id) {
      loadContractData(contractData._id)
    } else if (draftContractId) {
      loadDraftContractData(draftContractId)
    }
  }, [isEditMode, isViewMode, contractData, draftContractId])

  const loadCustomers = async () => {
    try {
      const response = await customerMasterService.getAllCustomers({ limit: 1000 })
      if (response.success) {
        setCustomers(response.data || [])
      }
    } catch (error) {
      console.error('Error loading customers:', error)
    }
  }

  const loadProjects = async () => {
    try {
      const response = await projectService.getAllProjects({ limit: 1000 })
      if (response.success) {
        setProjects(response.data || [])
      }
    } catch (error) {
      console.error('Error loading projects:', error)
    }
  }

  const loadDraftContracts = async () => {
    try {
      const response = await draftContractService.getAllDraftContracts({ 
        status: 'Approved',
        limit: 1000 
      })
      if (response.success) {
        setDraftContracts(response.data || [])
      }
    } catch (error) {
      console.error('Error loading draft contracts:', error)
    }
  }

  const loadDraftContractData = async (id) => {
    try {
      setIsLoading(true)
      const response = await draftContractService.getDraftContractById(id)
      if (response.success && response.data) {
        const draft = response.data
        setFormData(prev => ({
          ...prev,
          draftContract: draft._id,
          contractTitle: draft.contractTitle || prev.contractTitle,
          contractDescription: draft.contractDescription || prev.contractDescription,
          contractType: draft.contractType || prev.contractType,
          partyType: draft.partyType || prev.partyType,
          party: draft.party?._id || draft.party || prev.party,
          partyName: draft.partyName || draft.party?.customerName || prev.partyName,
          partyCode: draft.partyCode || draft.party?.customerCode || prev.partyCode,
          partyContactPerson: draft.partyContactPerson || prev.partyContactPerson,
          partyEmail: draft.partyEmail || draft.party?.email || prev.partyEmail,
          partyAddress: draft.partyAddress || prev.partyAddress,
          contractStartDate: draft.proposedStartDate ? new Date(draft.proposedStartDate).toISOString().split('T')[0] : prev.contractStartDate,
          contractEndDate: draft.proposedEndDate ? new Date(draft.proposedEndDate).toISOString().split('T')[0] : prev.contractEndDate,
          contractDuration: draft.proposedDuration || prev.contractDuration,
          contractValue: draft.contractValue || prev.contractValue,
          currency: draft.currency || prev.currency,
          paymentTerms: draft.paymentTerms || prev.paymentTerms,
          billingFrequency: draft.billingFrequency || prev.billingFrequency,
          project: draft.project?._id || draft.project || prev.project,
          projectCode: draft.projectCode || draft.project?.projectCode || prev.projectCode,
          projectName: draft.projectName || draft.project?.projectName || prev.projectName,
          termsAndConditions: draft.termsAndConditions || prev.termsAndConditions,
          keyTerms: draft.keyTerms || prev.keyTerms,
          deliverables: draft.deliverables || prev.deliverables,
          milestones: draft.milestones || prev.milestones,
          complianceRequirements: draft.complianceRequirements || prev.complianceRequirements
        }))
      }
    } catch (error) {
      console.error('Error loading draft contract data:', error)
      toast.error('Failed to load draft contract data')
    } finally {
      setIsLoading(false)
    }
  }

  const loadContractData = async (id) => {
    try {
      setIsLoading(true)
      const response = await signedContractService.getSignedContractById(id)
      if (response.success && response.data) {
        const contract = response.data
        setFormData({
          contractNumber: contract.contractNumber || '',
          draftContract: contract.draftContract?._id || contract.draftContract || '',
          contractTitle: contract.contractTitle || '',
          contractDescription: contract.contractDescription || '',
          contractType: contract.contractType || 'Service Agreement',
          partyType: contract.partyType || 'Customer',
          party: contract.party?._id || contract.party || '',
          partyName: contract.partyName || contract.party?.customerName || '',
          partyCode: contract.partyCode || contract.party?.customerCode || '',
          partyContactPerson: contract.partyContactPerson || '',
          partyEmail: contract.partyEmail || contract.party?.email || '',
          partyAddress: contract.partyAddress || {
            street: '',
            city: '',
            state: '',
            country: '',
            zipCode: ''
          },
          contractStartDate: contract.contractStartDate ? new Date(contract.contractStartDate).toISOString().split('T')[0] : '',
          contractEndDate: contract.contractEndDate ? new Date(contract.contractEndDate).toISOString().split('T')[0] : '',
          contractDuration: contract.contractDuration || 0,
          signedDate: contract.signedDate ? new Date(contract.signedDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
          signedByName: contract.signedByName || '',
          partySignedDate: contract.partySignedDate ? new Date(contract.partySignedDate).toISOString().split('T')[0] : '',
          partySignedBy: contract.partySignedBy || '',
          partySignedByName: contract.partySignedByName || '',
          contractValue: contract.contractValue || 0,
          currency: contract.currency || 'INR',
          paymentTerms: contract.paymentTerms || '',
          billingFrequency: contract.billingFrequency || 'Monthly',
          project: contract.project?._id || contract.project || '',
          projectCode: contract.projectCode || contract.project?.projectCode || '',
          projectName: contract.projectName || contract.project?.projectName || '',
          termsAndConditions: contract.termsAndConditions || '',
          keyTerms: contract.keyTerms || [],
          deliverables: contract.deliverables || [],
          milestones: contract.milestones || [],
          complianceRequirements: contract.complianceRequirements || [],
          complianceStatus: contract.complianceStatus || 'Compliant',
          lastComplianceReview: toDateInput(contract.lastComplianceReview),
          nextComplianceReview: toDateInput(contract.nextComplianceReview),
          isRenewable: contract.isRenewable || false,
          renewalDate: contract.renewalDate ? new Date(contract.renewalDate).toISOString().split('T')[0] : '',
          renewalNoticePeriod: contract.renewalNoticePeriod || 30,
          autoRenewal: contract.autoRenewal || false,
          notes: contract.notes || '',
          internalNotes: contract.internalNotes || '',
          status: contract.status || 'Active'
        })
      }
    } catch (error) {
      console.error('Error loading contract data:', error)
      toast.error('Failed to load contract data')
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    
    if (name.startsWith('partyAddress.')) {
      const addressField = name.split('.')[1]
      setFormData(prev => ({
        ...prev,
        partyAddress: {
          ...prev.partyAddress,
          [addressField]: value
        }
      }))
    } else if (name === 'party') {
      const selectedCustomer = customers.find(cust => cust._id === value)
      setFormData(prev => ({
        ...prev,
        party: value,
        partyName: selectedCustomer?.customerName || '',
        partyCode: selectedCustomer?.customerCode || '',
        partyEmail: selectedCustomer?.email || '',
        partyAddress: selectedCustomer?.address || prev.partyAddress
      }))
    } else if (name === 'project') {
      const selectedProject = projects.find(proj => proj._id === value)
      setFormData(prev => ({
        ...prev,
        project: value,
        projectCode: selectedProject?.projectNumber || '',
        projectName: selectedProject?.projectName || ''
      }))
    } else if (name === 'draftContract') {
      loadDraftContractData(value)
    } else if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }))
    } else if (type === 'number') {
      setFormData(prev => ({
        ...prev,
        [name]: parseFloat(value) || 0
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }))
    }

    // Calculate duration if dates changed
    if (name === 'contractStartDate' || name === 'contractEndDate') {
      const startDate = name === 'contractStartDate' ? value : formData.contractStartDate
      const endDate = name === 'contractEndDate' ? value : formData.contractEndDate
      if (startDate && endDate) {
        const start = new Date(startDate)
        const end = new Date(endDate)
        const diffTime = Math.abs(end - start)
        const diffMonths = Math.ceil(diffTime / (1000 * 60 * 60 * 24 * 30))
        setFormData(prev => ({
          ...prev,
          contractDuration: diffMonths
        }))
      }
    }
  }

  const addKeyTerm = () => {
    if (newKeyTerm.term.trim()) {
      setFormData(prev => ({
        ...prev,
        keyTerms: [...prev.keyTerms, { ...newKeyTerm }]
      }))
      setNewKeyTerm({ term: '', description: '' })
    }
  }

  const removeKeyTerm = (index) => {
    setFormData(prev => ({
      ...prev,
      keyTerms: prev.keyTerms.filter((_, i) => i !== index)
    }))
  }

  const addDeliverable = () => {
    if (newDeliverable.trim()) {
      setFormData(prev => ({
        ...prev,
        deliverables: [...prev.deliverables, newDeliverable.trim()]
      }))
      setNewDeliverable('')
    }
  }

  const removeDeliverable = (index) => {
    setFormData(prev => ({
      ...prev,
      deliverables: prev.deliverables.filter((_, i) => i !== index)
    }))
  }

  const addMilestone = () => {
    if (newMilestone.milestoneName.trim()) {
      setFormData(prev => ({
        ...prev,
        milestones: [...prev.milestones, { ...newMilestone }]
      }))
      setNewMilestone({ milestoneName: '', milestoneDate: '', milestoneAmount: 0, description: '', status: 'Pending' })
    }
  }

  const removeMilestone = (index) => {
    setFormData(prev => ({
      ...prev,
      milestones: prev.milestones.filter((_, i) => i !== index)
    }))
  }

  const addCompliance = () => {
    if (newCompliance.trim()) {
      setFormData(prev => ({
        ...prev,
        complianceRequirements: [...prev.complianceRequirements, newCompliance.trim()]
      }))
      setNewCompliance('')
    }
  }

  const removeCompliance = (index) => {
    setFormData(prev => ({
      ...prev,
      complianceRequirements: prev.complianceRequirements.filter((_, i) => i !== index)
    }))
  }

  const handleSave = async () => {
    try {
      // Validation
      if (!formData.contractTitle || !formData.partyName || !formData.contractStartDate || !formData.signedDate) {
        toast.error('Please fill in all required fields (Contract Title, Party, Start Date, and Signed Date)')
        return
      }

      setIsLoading(true)
      
      if (isEditMode && contractData?._id) {
        const response = await signedContractService.updateSignedContract(contractData._id, formData)
        if (response.success) {
          toast.success('Signed contract updated successfully!')
          navigate('/contracts/signed-contract')
        } else {
          toast.error(response.message || 'Failed to update signed contract')
        }
      } else {
        const response = await signedContractService.createSignedContract(formData)
        if (response.success) {
          toast.success('Signed contract created successfully!')
          navigate('/contracts/signed-contract')
        } else {
          toast.error(response.message || 'Failed to create signed contract')
        }
      }
    } catch (error) {
      console.error('Error saving signed contract:', error)
      if (error.response?.status === 400) {
        toast.error(error.response.data?.message || 'Invalid data provided')
      } else {
        if (!error._errorHandled) {
          toast.error('Failed to save signed contract')
        }
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleReset = () => {
    if (isEditMode || isViewMode) {
      loadContractData(contractData._id)
    } else if (draftContractId) {
      loadDraftContractData(draftContractId)
    } else {
      setFormData(prev => ({
        ...prev,
        contractTitle: '',
        contractDescription: '',
        contractType: 'Service Agreement',
        partyType: 'Customer',
        party: '',
        partyName: '',
        partyCode: '',
        partyContactPerson: '',
        partyEmail: '',
        partyAddress: {
          street: '',
          city: '',
          state: '',
          country: '',
          zipCode: ''
        },
        contractStartDate: '',
        contractEndDate: '',
        contractDuration: 0,
        signedDate: new Date().toISOString().split('T')[0],
        signedByName: '',
        partySignedDate: '',
        partySignedBy: '',
        partySignedByName: '',
        contractValue: 0,
        paymentTerms: '',
        project: '',
        projectCode: '',
        projectName: '',
        termsAndConditions: '',
        keyTerms: [],
        deliverables: [],
        milestones: [],
        complianceRequirements: [],
        complianceStatus: 'Compliant',
        lastComplianceReview: '',
        nextComplianceReview: '',
        isRenewable: false,
        renewalDate: '',
        renewalNoticePeriod: 30,
        autoRenewal: false,
        notes: '',
        internalNotes: '',
        status: 'Active'
      }))
    }
  }

  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'Contracts', path: '/contracts' },
    { label: 'Signed Contracts', path: '/contracts/signed-contract' },
    { 
      label: isEditMode ? 'Edit Signed Contract' : isViewMode ? 'View Signed Contract' : 'New Signed Contract', 
      path: location.pathname, 
      isCurrent: true 
    }
  ]

  if (isLoading && (isEditMode || isViewMode || draftContractId)) {
    return (
      <div className="sales-dashboard">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p style={{ marginTop: '16px', color: 'var(--color-text-secondary, #5B6478)' }}>Loading contract...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="sales-dashboard">
      <Breadcrumb items={breadcrumbItems} />

      <div className="page-header">
        <div className="page-title">
          <h1>{isEditMode ? 'Edit Signed Contract' : isViewMode ? 'View Signed Contract' : 'New Signed Contract'}</h1>
          <p>{isEditMode ? 'Update signed contract details' : isViewMode ? 'View signed contract details' : 'Create a new signed contract'}</p>
        </div>
        <div className="page-actions">
          <button 
            className="btn btn-secondary" 
            onClick={() => navigate('/contracts/signed-contract')}
            style={{
              padding: '10px 20px',
              border: '1px solid #e9ecef',
              borderRadius: '6px',
              background: '#fff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontFamily: 'Inter, sans-serif',
              fontSize: '14px',
              color: '#495057'
            }}
          >
            <ArrowLeft size={16} />
            Back
          </button>
        </div>
      </div>

      <div className="form-container">
        {/* Draft Contract Selection (only for new contracts) */}
        {!isEditMode && !isViewMode && (
          <div className="form-content" style={{ paddingBottom: '12px' }}>
            <div className="section-header">
              <h3>Source Contract</h3>
            </div>
            <div className="form-group">
              <label htmlFor="draftContract" className="form-label">
                Create from Draft Contract (Optional)
              </label>
              <select
                id="draftContract"
                name="draftContract"
                value={formData.draftContract}
                onChange={handleInputChange}
                className="form-input"
              >
                <option value="">Create New Contract</option>
                {draftContracts.map(draft => (
                  <option key={draft._id} value={draft._id}>
                    {draft.contractNumber} - {draft.contractTitle}
                  </option>
                ))}
              </select>
              <small style={{ color: 'var(--color-text-secondary, #5B6478)', fontSize: '12px' }}>
                Select a draft contract to auto-populate fields
              </small>
            </div>
          </div>
        )}

        <div className="sac-form-header">
          <h2>Contract Information</h2>
        </div>
          
        <form className="form-content">
          <div className="section-header">
            <h3>Contract Details</h3>
          </div>

          {/* Row 1: Contract Number, Contract Title, Contract Type, Status */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="contractNumber" className="form-label required">
                Contract Number
              </label>
              <input
                type="text"
                id="contractNumber"
                name="contractNumber"
                value={isLoadingContractNumber ? 'Generating...' : formData.contractNumber}
                readOnly
                className="form-input"
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="contractTitle" className="form-label required">
                Contract Title
              </label>
              <input
                type="text"
                id="contractTitle"
                name="contractTitle"
                value={formData.contractTitle}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                required
                placeholder="e.g., Service Agreement with ABC Corp"
              />
            </div>

            <div className="form-group">
              <label htmlFor="contractType" className="form-label">
                Contract Type
              </label>
              <select
                id="contractType"
                name="contractType"
                value={formData.contractType}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              >
                {contractTypeOptions.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="status" className="form-label">
                Status
              </label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              >
                <option value="Active">Active</option>
                <option value="Expired">Expired</option>
                <option value="Terminated">Terminated</option>
                <option value="Suspended">Suspended</option>
                <option value="Renewed">Renewed</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </div>
          </div>

          {/* Contract Description */}
          <div className="form-group">
            <label htmlFor="contractDescription" className="form-label">
              Contract Description
            </label>
            <textarea
              id="contractDescription"
              name="contractDescription"
              value={formData.contractDescription}
              onChange={handleInputChange}
              disabled={isViewMode}
              className="form-input"
              rows="3"
              placeholder="Brief description of the contract..."
            />
          </div>

          {/* Signature Information */}
          <div className="form-section-divider">
            <div className="section-header">
              <h3>Signature Information</h3>
            </div>
          </div>

          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="signedDate" className="form-label required">
                Signed Date
              </label>
              <input
                type="date"
                id="signedDate"
                name="signedDate"
                value={formData.signedDate}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="signedByName" className="form-label">
                Signed By (Our Company)
              </label>
              <input
                type="text"
                id="signedByName"
                name="signedByName"
                value={formData.signedByName}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                placeholder="Signatory name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="partySignedDate" className="form-label">
                Party Signed Date
              </label>
              <input
                type="date"
                id="partySignedDate"
                name="partySignedDate"
                value={formData.partySignedDate}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="partySignedByName" className="form-label">
                Party Signed By
              </label>
              <input
                type="text"
                id="partySignedByName"
                name="partySignedByName"
                value={formData.partySignedByName}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                placeholder="Party signatory name"
              />
            </div>
          </div>

          {/* Party Information Section */}
          <div className="form-section-divider">
            <div className="section-header">
              <h3>Party Information</h3>
            </div>
          </div>

          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="partyType" className="form-label">
                Party Type
              </label>
              <select
                id="partyType"
                name="partyType"
                value={formData.partyType}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              >
                <option value="Customer">Customer</option>
                <option value="Vendor">Vendor</option>
                <option value="Partner">Partner</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="party" className="form-label required">
                Party
              </label>
              <select
                id="party"
                name="party"
                value={formData.party}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                required
              >
                <option value="">Select {formData.partyType}</option>
                {customers.map(cust => (
                  <option key={cust._id} value={cust._id}>
                    {cust.customerName} ({cust.customerCode})
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="partyContactPerson" className="form-label">
                Contact Person
              </label>
              <input
                type="text"
                id="partyContactPerson"
                name="partyContactPerson"
                value={formData.partyContactPerson}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                placeholder="Contact person name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="partyEmail" className="form-label">
                Email
              </label>
              <input
                type="email"
                id="partyEmail"
                name="partyEmail"
                value={formData.partyEmail}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                placeholder="party@example.com"
              />
            </div>
          </div>

          {/* Contract Period */}
          <div className="form-section-divider">
            <div className="section-header">
              <h3>Contract Period</h3>
            </div>
          </div>

          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="contractStartDate" className="form-label required">
                Contract Start Date
              </label>
              <input
                type="date"
                id="contractStartDate"
                name="contractStartDate"
                value={formData.contractStartDate}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="contractEndDate" className="form-label">
                Contract End Date
              </label>
              <input
                type="date"
                id="contractEndDate"
                name="contractEndDate"
                value={formData.contractEndDate}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="contractDuration" className="form-label">
                Duration (Months)
              </label>
              <input
                type="number"
                id="contractDuration"
                name="contractDuration"
                value={formData.contractDuration}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                min="0"
                readOnly
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)' }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="isRenewable" className="form-label">
                Renewable
              </label>
              <div style={{ display: 'flex', alignItems: 'center', marginTop: '8px' }}>
                <input
                  type="checkbox"
                  id="isRenewable"
                  name="isRenewable"
                  checked={formData.isRenewable}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={{ width: '18px', height: '18px', cursor: isViewMode ? 'not-allowed' : 'pointer' }}
                />
                <label htmlFor="isRenewable" style={{ marginLeft: '8px', fontSize: '14px', color: '#495057' }}>
                  Contract is renewable
                </label>
              </div>
            </div>
          </div>

          {formData.isRenewable && (
            <div className="grid-row-4">
              <div className="form-group">
                <label htmlFor="renewalDate" className="form-label">
                  Renewal Date
                </label>
                <input
                  type="date"
                  id="renewalDate"
                  name="renewalDate"
                  value={formData.renewalDate}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="renewalNoticePeriod" className="form-label">
                  Renewal Notice Period (Days)
                </label>
                <input
                  type="number"
                  id="renewalNoticePeriod"
                  name="renewalNoticePeriod"
                  value={formData.renewalNoticePeriod}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  className="form-input"
                  min="0"
                />
              </div>

              <div className="form-group">
                <label htmlFor="autoRenewal" className="form-label">
                  Auto Renewal
                </label>
                <div style={{ display: 'flex', alignItems: 'center', marginTop: '8px' }}>
                  <input
                    type="checkbox"
                    id="autoRenewal"
                    name="autoRenewal"
                    checked={formData.autoRenewal}
                    onChange={handleInputChange}
                    disabled={isViewMode}
                    style={{ width: '18px', height: '18px', cursor: isViewMode ? 'not-allowed' : 'pointer' }}
                  />
                  <label htmlFor="autoRenewal" style={{ marginLeft: '8px', fontSize: '14px', color: '#495057' }}>
                    Auto-renew contract
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Compliance */}
          <div className="form-section-divider">
            <div className="section-header">
              <h3>Compliance</h3>
            </div>
          </div>

          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="complianceStatus" className="form-label">
                Compliance Status
              </label>
              <select
                id="complianceStatus"
                name="complianceStatus"
                value={formData.complianceStatus}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              >
                {COMPLIANCE_STATUS_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="lastComplianceReview" className="form-label">
                Last Compliance Review
              </label>
              <input
                type="date"
                id="lastComplianceReview"
                name="lastComplianceReview"
                value={formData.lastComplianceReview}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="nextComplianceReview" className="form-label">
                Next Compliance Review
              </label>
              <input
                type="date"
                id="nextComplianceReview"
                name="nextComplianceReview"
                value={formData.nextComplianceReview}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                min={formData.lastComplianceReview || undefined}
              />
            </div>
          </div>

          {/* Financial Information */}
          <div className="form-section-divider">
            <div className="section-header">
              <h3>Financial Information</h3>
            </div>
          </div>

          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="contractValue" className="form-label">
                Contract Value
              </label>
              <input
                type="number"
                id="contractValue"
                name="contractValue"
                value={formData.contractValue}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                min="0"
                step="0.01"
              />
            </div>

            <div className="form-group">
              <label htmlFor="currency" className="form-label">
                Currency
              </label>
              <select
                id="currency"
                name="currency"
                value={formData.currency}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              >
                <option value="INR">INR</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="billingFrequency" className="form-label">
                Billing Frequency
              </label>
              <select
                id="billingFrequency"
                name="billingFrequency"
                value={formData.billingFrequency}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              >
                <option value="One-time">One-time</option>
                <option value="Monthly">Monthly</option>
                <option value="Quarterly">Quarterly</option>
                <option value="Annually">Annually</option>
                <option value="Milestone-based">Milestone-based</option>
                <option value="Custom">Custom</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="project" className="form-label">
                Project
              </label>
              <select
                id="project"
                name="project"
                value={formData.project}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              >
                <option value="">Select Project</option>
                {projects.map(proj => (
                  <option key={proj._id} value={proj._id}>
                    {proj.projectName} ({proj.projectNumber})
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="paymentTerms" className="form-label">
              Payment Terms
            </label>
            <textarea
              id="paymentTerms"
              name="paymentTerms"
              value={formData.paymentTerms}
              onChange={handleInputChange}
              disabled={isViewMode}
              className="form-input"
              rows="2"
              placeholder="e.g., Net 30, 50% advance, etc."
            />
          </div>

          {/* Key Terms, Deliverables, Milestones, Compliance - Similar to Draft Contract Form */}
          {/* For brevity, I'll include a simplified version - you can expand these sections similar to DraftContractForm */}
          
          <div className="form-group">
            <label htmlFor="termsAndConditions" className="form-label">
              Terms and Conditions
            </label>
            <textarea
              id="termsAndConditions"
              name="termsAndConditions"
              value={formData.termsAndConditions}
              onChange={handleInputChange}
              disabled={isViewMode}
              className="form-input"
              rows="5"
              placeholder="Enter contract terms and conditions..."
            />
          </div>

          {/* Notes */}
          <div className="form-group">
            <label htmlFor="notes" className="form-label">
              Notes
            </label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              disabled={isViewMode}
              className="form-input"
              rows="3"
              placeholder="General notes..."
            />
          </div>

          <div className="form-group">
            <label htmlFor="internalNotes" className="form-label">
              Internal Notes
            </label>
            <textarea
              id="internalNotes"
              name="internalNotes"
              value={formData.internalNotes}
              onChange={handleInputChange}
              disabled={isViewMode}
              className="form-input"
              rows="3"
              placeholder="Internal notes (not visible to party)..."
            />
          </div>

          {/* Action Buttons */}
          {!isViewMode && (
            <div className="form-actions">
              <button
                type="button"
                onClick={handleSave}
                disabled={isLoading}
                className="btn btn-primary"
                style={{
                  padding: '10px 24px',
                  background: '#292752',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '14px',
                  fontWeight: '500',
                  opacity: isLoading ? 0.6 : 1
                }}
              >
                <Save size={16} />
                {isLoading ? 'Saving...' : 'Save'}
              </button>
              <button
                type="button"
                onClick={handleReset}
                disabled={isLoading}
                className="btn btn-secondary"
                style={{
                  padding: '10px 24px',
                  border: '1px solid #e9ecef',
                  borderRadius: '6px',
                  background: '#fff',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '14px',
                  color: '#495057'
                }}
              >
                <RotateCcw size={16} />
                Reset
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  )
}

export default SignedContractForm

